/**
 * Il sonar: dove finisce ogni CI sullo schermo, e di che colore.
 *
 * ## A cosa serve davvero
 *
 * A far **saltare all'occhio ciò che è nuovo**. Quando un parco è a regime, la
 * dashboard è una schermata di numeri che non cambiano, e un CI comparso
 * un'ora fa — da una scansione o da una conversazione NetFlow — è una riga in
 * più in un elenco lungo. Nessuno la nota. Sul sonar è un punto rosso che
 * lampeggia in mezzo a punti verdi fermi, e si nota dall'altra parte della
 * stanza.
 *
 * ## Perché la logica sta qui e non nel componente
 *
 * Perché è la parte che si sbaglia, ed è l'unica verificabile senza montare un
 * browser. In questo progetto è già successo che un difetto sopravvivesse a due
 * giri di correzioni perché stava dentro un componente.
 *
 * ## Le posizioni sono **deterministiche**
 *
 * Derivate dall'identificativo del CI, non estratte a caso. La dashboard si
 * ricarica da sola ogni dieci secondi: con posizioni casuali il quadro si
 * rimescolerebbe a ogni giro, e il punto che si stava guardando sarebbe
 * altrove. È lo stesso motivo per cui la mappa della topologia ha una
 * disposizione deterministica.
 */

/**
 * Quale dei due quadri è acceso: **uno solo**, o nessuno.
 *
 * Sono due letture della stessa cosa — il radar dice dove, il sonar dice cosa
 * c'è là sotto — e vanno guardate una per volta: aperti insieme occupano due
 * schermate e la dashboard, che è il posto dove si arriva per sapere in fretta
 * come va, diventa la cosa da scorrere per prima.
 *
 * Un solo valore invece di due interruttori indipendenti: con due booleani
 * «accendine uno» diventa «ricordati di spegnere l'altro», e chi aggiunge il
 * terzo quadro fra sei mesi non se lo ricorda.
 */
import { t } from './i18n/lingua'

export type Quadro = 'nessuno' | 'sonar' | 'radar'

/** Accende quello chiesto; se era già acceso, spegne. */
export function toggle(current: Quadro, wanted: Exclude<Quadro, 'nessuno'>): Quadro {
  return current === wanted ? 'nessuno' : wanted
}

export interface SonarItem {
  id: string
  name: string | null
  ci_type: string
  ci_class?: string | null
  first_seen?: string | null
}

export interface Blip {
  id: string
  label: string
  /** Frazione del raggio, 0 al centro e 1 sul bordo. */
  radius: number
  /** Radianti. */
  angle: number
  /** In percentuale del riquadro, pronti per il CSS. */
  x: number
  y: number
  /** `nuovo` lampeggia, `noto` no. */
  state: 'nuovo' | 'noto'
  /** Perché è così: finisce nel titolo del punto. */
  why: string
}

/**
 * Un CI è «nuovo» finché non lo si è riconosciuto **o** finché è appena
 * comparso.
 *
 * Due condizioni e non una, perché rispondono a due allarmi diversi:
 *
 * - **non classificato**: la piattaforma non sa cosa sia. Va guardato, e finché
 *   nessuno lo guarda resta rosso — anche fra un mese;
 * - **comparso da poco**: la piattaforma sa cosa sia, ma è arrivato adesso. È
 *   il caso del NetFlow che scopre una macchina che nessuno aveva scansionato,
 *   e resta rosso per il tempo in cui la notizia è ancora una notizia.
 */
export const UNKNOWN_CLASSES = new Set(['', 'unknown', 'non classificato'])
export const UNKNOWN_TYPES = new Set(['unmanaged_host'])

/**
 * Oggetti **derivati** da una fonte già nota: il VIP o l'IP d'interfaccia di un
 * apparato (indirizzo aggiuntivo, col suo proprietario), il cluster dichiarato
 * dai suoi nodi. Nascono insieme a qualcosa che la piattaforma conosce, e non
 * sono una novità da riconoscere: contarli «comparsi da poco» riempiva il radar
 * di rosso per ogni cluster inventariato (caso dal campo, 26/09/2026).
 */
export const DERIVED_TYPES = new Set(['network_address', 'compute_cluster'])

/** Per quanto tempo un CI appena visto conta come «nuovo». */
export const NEW_FOR_HOURS = 24

export function isNew(item: SonarItem, now: number): { state: Blip['state']; why: string } {
  const klass = (item.ci_class ?? '').toLowerCase()
  if (UNKNOWN_CLASSES.has(klass)) {
    return { state: 'nuovo', why: t('non classificato: la piattaforma non sa cosa sia') }
  }
  if (UNKNOWN_TYPES.has(item.ci_type)) {
    return { state: 'nuovo', why: t('host non gestito: vivo in rete ma non interrogabile') }
  }
  if (item.first_seen && !DERIVED_TYPES.has(item.ci_type)) {
    const seen = Date.parse(item.first_seen)
    if (Number.isFinite(seen) && now - seen < NEW_FOR_HOURS * 3600_000) {
      return { state: 'nuovo', why: t('comparso nelle ultime 24 ore') }
    }
  }
  return { state: 'noto', why: t('riconosciuto e classificato') }
}

/**
 * Un numero stabile fra 0 e 1 a partire da una stringa.
 *
 * FNV-1a: poche righe, nessuna dipendenza, e distribuisce abbastanza bene da
 * non ammassare i punti in un settore. Non è crittografia — serve solo che lo
 * stesso CI finisca sempre nello stesso posto.
 */
export function spread(seed: string): number {
  let hash = 2166136261
  for (let i = 0; i < seed.length; i += 1) {
    hash ^= seed.charCodeAt(i)
    hash = Math.imul(hash, 16777619)
  }
  return ((hash >>> 0) % 100000) / 100000
}

/**
 * Da CI a punti sullo schermo.
 *
 * I nuovi stanno **più vicini al centro**: è dove l'occhio va per primo, ed è
 * il posto giusto per ciò che chiede attenzione. I noti si distribuiscono verso
 * il bordo, che è anche il modo di lasciare respiro ai rossi quando sono pochi
 * in mezzo a molti verdi.
 */
export function blips(items: SonarItem[], now: number): Blip[] {
  return items.map((item) => {
    const { state, why } = isNew(item, now)
    const angle = spread(item.id) * Math.PI * 2
    // Radice quadrata: senza, i punti si addenserebbero al centro, perché a
    // raggio crescente corrisponde più area. È lo stesso motivo per cui una
    // distribuzione uniforme su un disco non è uniforme sul raggio.
    const spreadOut = Math.sqrt(spread(`${item.id}:r`))
    const radius = state === 'nuovo' ? 0.15 + spreadOut * 0.45 : 0.42 + spreadOut * 0.53
    return {
      id: item.id,
      label: item.name || item.ci_type,
      radius,
      angle,
      x: 50 + Math.cos(angle) * radius * 50,
      y: 50 + Math.sin(angle) * radius * 50,
      state,
      why,
    }
  })
}

/** Quanti sono, per la riga che accompagna il quadro. */
export function tally(list: Blip[]): { nuovi: number; noti: number } {
  return {
    nuovi: list.filter((b) => b.state === 'nuovo').length,
    noti: list.filter((b) => b.state === 'noto').length,
  }
}

// --------------------------------------------------------------- il banco
//
// Il quadro tondo dice **dove**; la scena sott'acqua dice **quanti** e come si
// muovono. Sono due letture della stessa cosa e restano entrambe: il quadro è
// lo strumento, la scena è ciò che lo strumento sta guardando.
//
// Le regole del quadro valgono identiche qui — posizioni deterministiche,
// elenco troncato dichiarato, il colore mai da solo — perché sono le stesse tre
// cose che si sbagliano.

export interface Fish {
  id: string
  label: string
  state: Blip['state']
  why: string
  /** Percentuale dall'alto della scena: la profondità a cui nuota. */
  depth: number
  /** Percentuale da sinistra: dov'è **al tempo zero**, e dove resta se le
   *  animazioni sono spente. */
  x: number
  /** `1` nuota verso destra, `-1` verso sinistra. */
  facing: 1 | -1
  /** Secondi per attraversare tutta la scena. */
  duration: number
  /**
   * Ritardo **negativo**: fa partire l'animazione già a metà traversata.
   *
   * Senza, all'accensione del sonar tutti i pesci partirebbero insieme dal
   * bordo sinistro in fila indiana — un'onda, non un banco. Il valore è
   * calcolato perché la posizione al tempo zero coincida con ``x``: è ciò che
   * rende la scena identica, ferma, per chi ha spento le animazioni.
   */
  delay: number
  /** Prospettiva: più in basso è più lontano, quindi più piccolo. */
  scale: number
}

/**
 * Quanti pesci si disegnano davvero.
 *
 * Trecento sagome animate sono trecento animazioni che il browser ricalcola a
 * ogni fotogramma, su una pagina che si ricarica da sola: su una macchina
 * qualunque la scena diventa a scatti, ed è esattamente ciò che deve *non*
 * essere. Il taglio non è silenzioso — vedi ``hidden``.
 */
export const MAX_FISH = 48

/** I bordi della traversata, in percentuale: fuori scena da entrambi i lati. */
export const SWIM_FROM = -12
export const SWIM_TO = 112

/**
 * Dove comincia il fondale, in percentuale dall'alto.
 *
 * Nessun pesce nuota più in basso: le rocce stanno **davanti** al banco, e un
 * CI disegnato là sotto sparirebbe dietro una pietra. Sparire non è un modo di
 * dire «catalogato» — è indistinguibile da «non c'è», che è la cosa che questo
 * quadro deve dire bene.
 */
export const SEABED_TOP = 80

function fishOf(item: SonarItem, state: Blip['state'], why: string): Fish {
  // I nuovi nuotano **in alto, sotto il sonar**: è dove punta il fascio ed è
  // dove va l'occhio per primo. I catalogati stanno più in basso, verso il
  // fondale, che è anche il modo di lasciare respiro ai rossi quando sono
  // pochi in mezzo a molti verdi.
  const depth =
    state === 'nuovo' ? 14 + spread(`${item.id}:d`) * 26 : 34 + spread(`${item.id}:d`) * 42
  const x = 3 + spread(`${item.id}:x`) * 94
  const facing: 1 | -1 = spread(`${item.id}:f`) < 0.5 ? -1 : 1
  const duration = 20 + spread(`${item.id}:s`) * 22
  const travelled =
    facing === 1 ? (x - SWIM_FROM) / (SWIM_TO - SWIM_FROM) : (SWIM_TO - x) / (SWIM_TO - SWIM_FROM)
  return {
    id: item.id,
    label: item.name || item.ci_type,
    state,
    why,
    depth,
    x,
    facing,
    duration,
    delay: -travelled * duration,
    scale: (1.08 - (depth / 100) * 0.5) * (0.88 + spread(`${item.id}:z`) * 0.26),
  }
}

/**
 * Da CI a pesci.
 *
 * I nuovi hanno la precedenza sul taglio: sono il motivo per cui il sonar
 * esiste, e nasconderne uno per fare posto a una macchina catalogata da due
 * anni sarebbe togliere proprio l'unica informazione che si stava cercando.
 * Quanti ne restano fuori lo dice ``hidden``, e la scena lo scrive.
 */
export function school(
  items: SonarItem[],
  now: number,
  max: number = MAX_FISH,
): { fish: Fish[]; hidden: number } {
  const seen = items.map((item) => ({ item, ...isNew(item, now) }))
  const order = seen.sort((a, b) => {
    if (a.state !== b.state) return a.state === 'nuovo' ? -1 : 1
    // A parità di stato, l'ordine dell'identificativo: un ordine stabile è ciò
    // che impedisce alla scena di rimescolarsi a ogni ricarica.
    return a.item.id < b.item.id ? -1 : a.item.id > b.item.id ? 1 : 0
  })
  const shown = order.slice(0, max)
  return {
    fish: shown.map(({ item, state, why }) => fishOf(item, state, why)),
    hidden: order.length - shown.length,
  }
}
